import { Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

import { SpinningIcon } from '@/components/animation/SpinningIcon/SpinningIcon';
import { Button } from '@/components/form/baseInputs/Button';
import { ArrowRightIcon } from '@/components/icons/ArrowRightIcon';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { Home01Icon } from '@/components/icons/Home01Icon';
import { RefreshCw05Icon } from '@/components/icons/RefreshCw05Icon';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { useHouseholdWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts';
import { ROUTE_KEYS } from '@/navigation/constants';
import { COLORS } from '@/styles/tokens/colors';
import { WorkflowTrackerKind, WorkflowTrackerStatus } from '@/types/schema';

import {
  dismissSmartReviewPresentation,
  getDismissedSmartReviewPresentationId,
} from './smartReviewOnboardingBannerDismissal';
import { SmartReviewOnboardingModal } from './SmartReviewOnboardingModal';
import {
  clearDismissedSmartReviewStep,
  getDismissedSmartReviewStep,
} from './smartReviewOnboardingStep';

interface SmartReviewOnboardingBannerProps {
  householdId: string;
  householdClients?: Array<{
    firstName: string;
    lastName: string;
    stateCode: string;
  }>;
  validationQuestionsAnsweredAt: Date | null;
  isWaitingForValidationQuestions: boolean;
}

function formatElapsedTime(startedAt: Date, now: number) {
  const elapsedSeconds = Math.max(
    0,
    Math.floor((now - startedAt.getTime()) / 1000)
  );
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function getBannerColors(isIncomplete: boolean, isComplete: boolean) {
  if (isIncomplete) {
    return {
      background: COLORS.FUNCTIONAL.WARNING[50],
      border: COLORS.FUNCTIONAL.WARNING[300],
      badge: COLORS.FUNCTIONAL.WARNING[200],
    };
  }
  if (isComplete) {
    return {
      background: COLORS.TEAL[50],
      border: COLORS.TEAL[300],
      badge: COLORS.TEAL[200],
    };
  }
  return {
    background: COLORS.BLUE[50],
    border: COLORS.BLUE[300],
    badge: COLORS.BLUE[200],
  };
}

export function SmartReviewOnboardingBanner({
  householdId,
  householdClients,
  validationQuestionsAnsweredAt,
  isWaitingForValidationQuestions,
}: SmartReviewOnboardingBannerProps) {
  const { trackers } = useHouseholdWorkflowTrackersContext();
  const { navigate } = useNavigateToRoute();
  const [now, setNow] = useState(Date.now);
  const [dismissedStep, setDismissedStep] = useState(() =>
    getDismissedSmartReviewStep(householdId)
  );
  const [dismissedPresentationId, setDismissedPresentationId] = useState(() =>
    getDismissedSmartReviewPresentationId(householdId)
  );
  const onboardingTrowser = useTrowser(TrowserKey.SmartReviewOnboarding);
  const isResumeTrowserOpen =
    onboardingTrowser.isOpen && onboardingTrowser.target === householdId;
  const wasResumeTrowserOpenRef = useRef(isResumeTrowserOpen);

  const activeTracker = useMemo(
    () =>
      trackers.find(
        (tracker) =>
          tracker.status === WorkflowTrackerStatus.Started &&
          (tracker.kind === WorkflowTrackerKind.EstateSnapshotGeneration ||
            tracker.kind ===
              WorkflowTrackerKind.HouseholdOnboardingCompletion ||
            tracker.kind ===
              WorkflowTrackerKind.PresentationTemplatePromptGeneration)
      ),
    [trackers]
  );

  const completedPresentationTracker = useMemo(
    () =>
      trackers.find(
        (tracker) =>
          tracker.kind ===
            WorkflowTrackerKind.PresentationTemplatePromptGeneration &&
          tracker.status === WorkflowTrackerStatus.Completed &&
          tracker.presentation?.id
      ),
    [trackers]
  );
  const presentationId = completedPresentationTracker?.presentation?.id;
  const isIncomplete = !activeTracker && !!dismissedStep;
  const isComplete =
    !activeTracker &&
    !isIncomplete &&
    !!presentationId &&
    presentationId !== dismissedPresentationId;

  useEffect(() => {
    if (!activeTracker) return;
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [activeTracker]);

  useEffect(() => {
    setDismissedStep(getDismissedSmartReviewStep(householdId));
    setDismissedPresentationId(
      getDismissedSmartReviewPresentationId(householdId)
    );
  }, [householdId]);

  useEffect(() => {
    const justClosed = wasResumeTrowserOpenRef.current && !isResumeTrowserOpen;
    wasResumeTrowserOpenRef.current = isResumeTrowserOpen;
    if (justClosed) {
      setDismissedStep(getDismissedSmartReviewStep(householdId));
    }
  }, [householdId, isResumeTrowserOpen]);

  if (
    (!!activeTracker && isWaitingForValidationQuestions) ||
    (!activeTracker && !isIncomplete && !isComplete)
  ) {
    return null;
  }

  const bannerColors = getBannerColors(isIncomplete, isComplete);
  const startedAt =
    activeTracker?.kind === WorkflowTrackerKind.HouseholdOnboardingCompletion
      ? (validationQuestionsAnsweredAt ?? activeTracker.startedAt)
      : activeTracker?.startedAt;

  const dismiss = () => {
    if (isIncomplete) {
      clearDismissedSmartReviewStep(householdId);
      setDismissedStep(null);
      return;
    }
    if (!presentationId) return;
    dismissSmartReviewPresentation(householdId, presentationId);
    setDismissedPresentationId(presentationId);
  };

  const closeResumeModal = () => {
    onboardingTrowser.close();
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{
          minHeight: isIncomplete || isComplete ? 52 : 48,
          px: 1.5,
          py: 1,
          border: `1px solid ${bannerColors.border}`,
          borderRadius: '4px',
          bgcolor: bannerColors.background,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 24,
              height: 24,
              borderRadius: '8px',
              bgcolor: bannerColors.badge,
            }}
          >
            {isIncomplete && (
              <Home01Icon size={16} color={COLORS.FUNCTIONAL.WARNING[700]} />
            )}
            {!isIncomplete && !isComplete && (
              <SpinningIcon
                Icon={RefreshCw05Icon}
                size={16}
                color={COLORS.BLUE[700]}
              />
            )}
            {isComplete && <CheckIcon size={16} color={COLORS.TEAL[700]} />}
          </Stack>
          <Typography variant="title-xs">
            {isIncomplete && 'Finish client setup'}
            {!isIncomplete && !isComplete && 'Completing client profile'}
            {isComplete && 'Client presentation created'}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          {!isIncomplete && !isComplete && startedAt && (
            <Typography variant="body-xs" sx={{ color: COLORS.TEXT.SECONDARY }}>
              {formatElapsedTime(startedAt, now)}
            </Typography>
          )}
          {(isIncomplete || isComplete) && (
            <Button variant="transparent" size="xs" onClick={dismiss}>
              Dismiss
            </Button>
          )}
          {isIncomplete && (
            <Button
              variant="primary"
              size="xs"
              onClick={() => onboardingTrowser.open({ target: householdId })}
            >
              Resume
            </Button>
          )}
          {isComplete && (
            <Button
              variant="primary"
              size="xs"
              endIcon={ArrowRightIcon}
              onClick={() => {
                dismiss();
                navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS, {
                  householdId,
                });
              }}
            >
              Open
            </Button>
          )}
        </Stack>
      </Stack>

      {isResumeTrowserOpen && dismissedStep && (
        <SmartReviewOnboardingModal
          isOpen
          onClose={closeResumeModal}
          initialHouseholdId={householdId}
          initialHouseholdClients={householdClients}
          initialStep={dismissedStep}
        />
      )}
    </>
  );
}
